import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Modal, ModalDialog, ModalClose, Input, Textarea,
  Checkbox, Stack, Select, Option, Grid, Card, Avatar, Chip, Divider
} from '@mui/joy';
import { supabase } from '../../supabaseClient';
import EnhancedButton from '../../components/EnhancedButton';
import EnhancedInput from '../../components/EnhancedInput';
import MetricCard from '../../components/MetricCard';
import LoadingSkeleton, { CardSkeleton } from '../../components/LoadingSkeleton';
import dayjs from 'dayjs';

export default function CalendarAdmin() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    is_recurring: false,
    recurrence_rule: '',
  });

  const currentYear = new Date().getFullYear();
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const [startDateParts, setStartDateParts] = useState({
    month: 1,
    day: 1,
    year: currentYear,
    hour: 12,
    minute: '00',
    ampm: 'AM',
  });

  const [endDateParts, setEndDateParts] = useState({
    month: 1,
    day: 1,
    year: currentYear,
    hour: 12,
    minute: '00',
    ampm: 'AM',
  });

  const loadEvents = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('calendar_events').select('*').order('start_time');
    if (!error) setEvents(data);
    setLoading(false);
  };
  useEffect(() => { loadEvents(); }, []);

  useEffect(() => {
    if (editingEvent) {
      setForm(editingEvent);
      if (editingEvent.start_time) {
        const start = dayjs(editingEvent.start_time);
        setStartDateParts({
          month: start.month() + 1,
          day: start.date(),
          year: start.year(),
          hour: start.format('h'),
          minute: start.format('mm'),
          ampm: start.format('A'),
        });
      }
      if (editingEvent.end_time) {
        const end = dayjs(editingEvent.end_time);
        setEndDateParts({
          month: end.month() + 1,
          day: end.date(),
          year: end.year(),
          hour: end.format('h'),
          minute: end.format('mm'),
          ampm: end.format('A'),
        });
      }
    }
  }, [editingEvent]);

  const handleSubmit = async () => {
    const startTime = dayjs(`${startDateParts.year}-${startDateParts.month}-${startDateParts.day} ${startDateParts.hour}:${startDateParts.minute} ${startDateParts.ampm}`, 'YYYY-M-D h:mm A').toISOString();
    const endTime = dayjs(`${endDateParts.year}-${endDateParts.month}-${endDateParts.day} ${endDateParts.hour}:${endDateParts.minute} ${endDateParts.ampm}`, 'YYYY-M-D h:mm A').toISOString();

    const payload = {
      ...form,
      start_time: startTime,
      end_time: endTime,
      recurrence_rule: form.is_recurring ? form.recurrence_rule : null
    };
    if (editingEvent) {
      await supabase.from('calendar_events').update(payload).eq('id', editingEvent.id);
    } else {
      await supabase.from('calendar_events').insert([payload]);
    }
    setOpenModal(false);
    setEditingEvent(null);
    await loadEvents();
  };

  const handleDelete = async (id) => {
    await supabase.from('calendar_events').delete().eq('id', id);
    await loadEvents();
  };

  // Helper functions
  const getEventInitials = (eventTitle) => {
    return eventTitle?.split(' ').map(word => word[0]).join('').toUpperCase().substring(0, 2) || 'EV';
  };

  const getEventColor = (index) => {
    const colors = ['#00A884', '#17a2b8', '#28a745', '#6c757d', '#ffc107', '#dc3545'];
    return colors[index % colors.length];
  };

  // Statistics
  const totalEvents = events.length;
  const upcomingEvents = events.filter(e => dayjs(e.start_time).isAfter(dayjs())).length;
  const recurringEvents = events.filter(e => e.is_recurring).length;
  const todayEvents = events.filter(e => dayjs(e.start_time).isSame(dayjs(), 'day')).length;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        {/* Analytics Loading */}
        <Box sx={{ mb: 4 }}>
          <LoadingSkeleton height="32px" width="200px" sx={{ mb: 3 }} />
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
          </Grid>
        </Box>
        
        {/* Events Loading */}
        <Box sx={{ mt: 4 }}>
          <Grid container spacing={3}>
            {[...Array(4)].map((_, i) => (
              <Grid key={i} xs={12} sm={6} md={4}><CardSkeleton /></Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Calendar Analytics */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          level="h4" 
          sx={{ 
            mb: 3, 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
          className="fade-in"
        >
          <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
            event
          </span>
          Calendar Analytics
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 4 }} className="fade-in-up">
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Total Events"
              value={totalEvents}
              icon="event"
              gradientColors={['#00A884', '#007E33']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Upcoming Events"
              value={upcomingEvents}
              icon="upcoming"
              gradientColors={['#17a2b8', '#138496']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Today's Events"
              value={todayEvents}
              icon="today"
              gradientColors={['#28a745', '#218838']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Recurring Events"
              value={recurringEvents}
              icon="repeat"
              gradientColors={['#6c757d', '#5a6268']}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Add Event Section */}
      <Card 
        sx={{ 
          p: 3, 
          mb: 4,
          background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.02) 0%, rgba(0, 126, 51, 0.02) 100%)',
          border: '1px solid',
          borderColor: 'success.200',
        }}
        className="fade-in"
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography 
              level="h4" 
              sx={{ 
                color: 'success.700',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 1
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                event_note
              </span>
              Calendar Event Management
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Create, edit, and manage calendar events and recurring schedules
            </Typography>
          </Box>
          
          <EnhancedButton
            gradient
            onClick={() => { 
              setForm({ title: '', description: '', start_time: '', end_time: '', is_recurring: false, recurrence_rule: '' }); 
              setStartDateParts({
                month: 1,
                day: 1,
                year: currentYear,
                hour: 12,
                minute: '00',
                ampm: 'AM',
              });
              setEndDateParts({
                month: 1,
                day: 1,
                year: currentYear,
                hour: 12,
                minute: '00',
                ampm: 'AM',
              });
              setOpenModal(true); 
            }}
            startDecorator={
              <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                add
              </span>
            }
          >
            Add Event
          </EnhancedButton>
        </Box>
      </Card>

      {/* Events Grid */}
      <Box className="fade-in-up">
        <Typography 
          level="h4" 
          sx={{ 
            mb: 3, 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
            calendar_view_month
          </span>
          Event Calendar ({totalEvents})
        </Typography>
        
        <Grid container spacing={3}>
          {events.map((event, index) => (
            <Grid key={event.id} xs={12} sm={6} md={4}>
              <Card 
                sx={{ 
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0, 168, 132, 0.15)',
                  }
                }}
                className="hover-lift"
              >
                {/* Event Header */}
                <Box 
                  sx={{ 
                    p: 3, 
                    background: `linear-gradient(135deg, ${getEventColor(index)}15 0%, ${getEventColor(index)}05 100%)`,
                    borderBottom: '1px solid',
                    borderColor: 'neutral.200'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: getEventColor(index),
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.875rem'
                      }}
                    >
                      {getEventInitials(event.title)}
                    </Avatar>
                    
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography 
                        level="title-sm" 
                        sx={{ 
                          color: 'success.700',
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {event.title || 'Unnamed Event'}
                      </Typography>
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        {dayjs(event.start_time).format('MMM D, YYYY')}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip 
                      size="sm" 
                      variant="soft" 
                      sx={{
                        bgcolor: `${getEventColor(index)}20`,
                        color: getEventColor(index)
                      }}
                      startDecorator={
                        <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>
                          schedule
                        </span>
                      }
                    >
                      {dayjs(event.start_time).format('h:mm A')} - {dayjs(event.end_time).format('h:mm A')}
                    </Chip>
                    {event.is_recurring && (
                      <Chip size="sm" variant="soft" color="warning">
                        <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>
                          repeat
                        </span>
                      </Chip>
                    )}
                  </Box>
                </Box>

                {/* Event Details */}
                <Box sx={{ p: 3 }}>
                  <Typography 
                    level="body-sm" 
                    sx={{ 
                      mb: 2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}
                  >
                    {event.description || 'No description provided'}
                  </Typography>

                  {event.is_recurring && (
                    <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 3 }}>
                      🔄 Recurring: {event.recurrence_rule}
                    </Typography>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <EnhancedButton
                      size="sm"
                      variant="outlined"
                      onClick={() => { setEditingEvent(event); setForm(event); setOpenModal(true); }}
                      startDecorator={
                        <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                          edit
                        </span>
                      }
                      sx={{ flex: 1 }}
                    >
                      Edit
                    </EnhancedButton>
                    
                    <EnhancedButton 
                      size="sm" 
                      color="danger" 
                      variant="outlined" 
                      onClick={() => handleDelete(event.id)}
                      startDecorator={
                        <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                          delete
                        </span>
                      }
                      sx={{ flex: 1 }}
                    >
                      Delete
                    </EnhancedButton>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Enhanced Event Modal */}
      <Modal open={openModal} onClose={() => { setOpenModal(false); setEditingEvent(null); }}>
        <ModalDialog 
          sx={{ 
            maxWidth: '700px',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(15px)',
            border: '1px solid',
            borderColor: 'success.200',
          }}
        >
          <ModalClose />
          <Typography 
            level="h4" 
            sx={{ 
              mb: 3,
              color: 'success.700',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
              {editingEvent ? 'edit' : 'event_note'}
            </span>
            {editingEvent ? 'Edit Event' : 'Add New Event'}
          </Typography>
          
          <Grid container spacing={2}>
            <Grid xs={12}>
              <EnhancedInput
                label="Event Title"
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="Team Meeting"
                required
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    event
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography level="body-sm" sx={{ mb: 1, fontWeight: 500 }}>Description</Typography>
                <Textarea
                  value={form.description || ''}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Event description and details..."
                  minRows={3}
                  sx={{
                    '&:focus-within': {
                      borderColor: 'success.500',
                      boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                    },
                  }}
                />
              </Box>
            </Grid>

            <Grid xs={12}>
              <Typography level="body-sm" sx={{ mb: 2, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>
                <span className="material-symbols-outlined" style={{ fontSize: '18px', fontFamily: 'Material Symbols Outlined' }}>
                  schedule
                </span>
                Start Time
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                <Select value={startDateParts.month} onChange={(e, val) => setStartDateParts({ ...startDateParts, month: val })} sx={{ '&:focus-within': { borderColor: 'success.500' }}}>
                  {months.map((m, i) => <Option key={i} value={i + 1}>{m}</Option>)}
                </Select>
                <Select value={startDateParts.day} onChange={(e, val) => setStartDateParts({ ...startDateParts, day: val })} sx={{ '&:focus-within': { borderColor: 'success.500' }}}>
                  {[...Array(31).keys()].map(i => <Option key={i} value={i + 1}>{i + 1}</Option>)}
                </Select>
                <Select value={startDateParts.year} onChange={(e, val) => setStartDateParts({ ...startDateParts, year: val })} sx={{ '&:focus-within': { borderColor: 'success.500' }}}>
                  {[...Array(5).keys()].map(i => {
                    const y = currentYear + i;
                    return <Option key={y} value={y}>{y}</Option>;
                  })}
                </Select>
                <Select value={startDateParts.hour} onChange={(e, val) => setStartDateParts({ ...startDateParts, hour: val })} sx={{ '&:focus-within': { borderColor: 'success.500' }}}>
                  {[...Array(12).keys()].map(i => <Option key={i + 1} value={i + 1}>{i + 1}</Option>)}
                </Select>
                <Select value={startDateParts.minute} onChange={(e, val) => setStartDateParts({ ...startDateParts, minute: val })} sx={{ '&:focus-within': { borderColor: 'success.500' }}}>
                  {['00', '15', '30', '45'].map(m => <Option key={m} value={m}>{m}</Option>)}
                </Select>
                <Select value={startDateParts.ampm} onChange={(e, val) => setStartDateParts({ ...startDateParts, ampm: val })} sx={{ '&:focus-within': { borderColor: 'success.500' }}}>
                  <Option value="AM">AM</Option>
                  <Option value="PM">PM</Option>
                </Select>
              </Stack>
            </Grid>

            <Grid xs={12}>
              <Typography level="body-sm" sx={{ mb: 2, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>
                <span className="material-symbols-outlined" style={{ fontSize: '18px', fontFamily: 'Material Symbols Outlined' }}>
                  schedule
                </span>
                End Time
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                <Select value={endDateParts.month} onChange={(e, val) => setEndDateParts({ ...endDateParts, month: val })} sx={{ '&:focus-within': { borderColor: 'success.500' }}}>
                  {months.map((m, i) => <Option key={i} value={i + 1}>{m}</Option>)}
                </Select>
                <Select value={endDateParts.day} onChange={(e, val) => setEndDateParts({ ...endDateParts, day: val })} sx={{ '&:focus-within': { borderColor: 'success.500' }}}>
                  {[...Array(31).keys()].map(i => <Option key={i} value={i + 1}>{i + 1}</Option>)}
                </Select>
                <Select value={endDateParts.year} onChange={(e, val) => setEndDateParts({ ...endDateParts, year: val })} sx={{ '&:focus-within': { borderColor: 'success.500' }}}>
                  {[...Array(5).keys()].map(i => {
                    const y = currentYear + i;
                    return <Option key={y} value={y}>{y}</Option>;
                  })}
                </Select>
                <Select value={endDateParts.hour} onChange={(e, val) => setEndDateParts({ ...endDateParts, hour: val })} sx={{ '&:focus-within': { borderColor: 'success.500' }}}>
                  {[...Array(12).keys()].map(i => <Option key={i + 1} value={i + 1}>{i + 1}</Option>)}
                </Select>
                <Select value={endDateParts.minute} onChange={(e, val) => setEndDateParts({ ...endDateParts, minute: val })} sx={{ '&:focus-within': { borderColor: 'success.500' }}}>
                  {['00', '15', '30', '45'].map(m => <Option key={m} value={m}>{m}</Option>)}
                </Select>
                <Select value={endDateParts.ampm} onChange={(e, val) => setEndDateParts({ ...endDateParts, ampm: val })} sx={{ '&:focus-within': { borderColor: 'success.500' }}}>
                  <Option value="AM">AM</Option>
                  <Option value="PM">PM</Option>
                </Select>
              </Stack>
            </Grid>

            <Grid xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Checkbox
                  checked={form.is_recurring}
                  onChange={e => setForm({ ...form, is_recurring: e.target.checked })}
                  sx={{ color: 'text.primary' }}
                />
                <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                  Recurring Event
                </Typography>
              </Box>
              {form.is_recurring && (
                <EnhancedInput
                  label="Recurrence Rule"
                  value={form.recurrence_rule || ''}
                  onChange={e => setForm({ ...form, recurrence_rule: e.target.value })}
                  placeholder="FREQ=WEEKLY;BYDAY=MO"
                  startDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                      repeat
                    </span>
                  }
                />
              )}
            </Grid>
            
            <Grid xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <EnhancedButton
                  variant="outlined"
                  onClick={() => { setOpenModal(false); setEditingEvent(null); }}
                >
                  Cancel
                </EnhancedButton>
                <EnhancedButton
                  gradient
                  onClick={handleSubmit}
                  startDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                      {editingEvent ? 'save' : 'add'}
                    </span>
                  }
                >
                  {editingEvent ? 'Update Event' : 'Create Event'}
                </EnhancedButton>
              </Box>
            </Grid>
          </Grid>
        </ModalDialog>
      </Modal>
    </Box>
  );
}
